import React, { useState } from 'react';
import { X } from 'lucide-react';

/**
 * IndicatorColorEditor — minimal modal for tweaking DBB/KC/VWAP band
 * colours. Persisted via prefs.indicator_colors (server-round-trip
 * handled by the parent). New colours take effect on the next plugin
 * re-mount — for instant feedback the parent calls `onApply` which
 * bumps a version integer that Crypto.jsx forwards to ChartCanvas so
 * the affected plugins release + re-attach.
 */
const DEFAULTS = {
  // Double Bollinger — 5 series
  dbb_upper2: 'rgba(167,139,250,0.55)',
  dbb_lower2: 'rgba(167,139,250,0.55)',
  dbb_upper1: 'rgba(0,229,255,0.85)',
  dbb_lower1: 'rgba(0,229,255,0.85)',
  dbb_mid:    'rgba(247,201,72,0.9)',
  // Keltner — 3 series
  kc_upper:   'rgba(0,255,136,0.55)',
  kc_mid:     'rgba(0,255,136,0.85)',
  kc_lower:   'rgba(0,255,136,0.55)',
  // VWAP + BB (kept for future extension)
  vwap:       '#f472b6',
  bb_upper:   'rgba(0,229,255,0.6)',
  bb_mid:     'rgba(247,201,72,0.9)',
  bb_lower:   'rgba(0,229,255,0.6)',
};

/** Extract just the R/G/B/A parts as a hex string for the native
 *  colour input. Preserves alpha via a separate slider. */
function rgbaToHex(rgba) {
  const m = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/.exec(rgba || '');
  if (!m) return { hex: rgba || '#00e5ff', alpha: 1 };
  const toHex = (n) => Number(n).toString(16).padStart(2, '0');
  return { hex: `#${toHex(m[1])}${toHex(m[2])}${toHex(m[3])}`, alpha: m[4] ? Number(m[4]) : 1 };
}
function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

const GROUPS = [
  { title: 'Double Bollinger', keys: ['dbb_upper2', 'dbb_upper1', 'dbb_mid', 'dbb_lower1', 'dbb_lower2'] },
  { title: 'Keltner Channel', keys: ['kc_upper', 'kc_mid', 'kc_lower'] },
];

export default function IndicatorColorEditor({ open, initial, onClose, onApply, onReset }) {
  const [draft, setDraft] = useState({ ...DEFAULTS, ...(initial || {}) });
  if (!open) return null;
  const setColor = (k, hex, alpha) => {
    // For solid # colours (VWAP) keep as-is; band-style keys re-encode alpha.
    if (k === 'vwap') setDraft({ ...draft, [k]: hex });
    else setDraft({ ...draft, [k]: hexToRgba(hex, alpha) });
  };
  return (
    <div
      data-testid="crypto-colors-modal"
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[var(--bg-panel)] border border-[var(--border-default)] rounded-md p-5 min-w-[480px] max-w-[560px] font-mono text-[11px] text-[var(--txt-primary)]"
      >
        <div className="flex items-center justify-between mb-3">
          <span className="font-display tracking-[0.3em] uppercase text-cyan-sys text-[12px]">// indicator colours</span>
          <button data-testid="crypto-colors-close" onClick={onClose} className="text-[var(--txt-secondary)] hover:text-sell">
            <X size={14} />
          </button>
        </div>
        <div className="space-y-4 max-h-[70vh] overflow-auto pr-1">
          {GROUPS.map((g) => (
            <div key={g.title}>
              <div className="text-[10px] tracking-widest uppercase text-cyan-sys/80 mb-1.5">{g.title}</div>
              <div className="grid grid-cols-1 gap-1.5">
                {g.keys.map((k) => {
                  const { hex, alpha } = rgbaToHex(draft[k]);
                  return (
                    <div key={k} className="flex items-center gap-2">
                      <span className="w-28 text-[10px] text-[var(--txt-secondary)] uppercase tracking-widest">{k.replace('_', ' ')}</span>
                      <input
                        data-testid={`crypto-color-${k}`}
                        type="color"
                        value={hex}
                        onChange={(e) => setColor(k, e.target.value, alpha)}
                        className="w-8 h-6 bg-transparent border border-[var(--border-default)] cursor-pointer"
                      />
                      <input
                        data-testid={`crypto-alpha-${k}`}
                        type="range" min="0.05" max="1" step="0.05"
                        value={alpha}
                        onChange={(e) => setColor(k, hex, Number(e.target.value))}
                        className="flex-1 accent-cyan-sys"
                      />
                      <span className="tabular-nums text-[10px] w-8 text-right">{alpha.toFixed(2)}</span>
                      <div className="w-12 h-4" style={{ background: draft[k] }} />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            data-testid="crypto-colors-reset"
            onClick={() => { setDraft({ ...DEFAULTS }); onReset?.(); }}
            className="px-3 py-1 border border-[var(--border-default)] text-[10px] tracking-widest uppercase hover:border-hold hover:text-hold"
          >
            reset defaults
          </button>
          <button
            data-testid="crypto-colors-apply"
            onClick={() => onApply?.(draft)}
            className="px-3 py-1 border border-cyan-sys text-cyan-sys bg-cyan-sys/10 text-[10px] tracking-widest uppercase"
          >
            apply
          </button>
        </div>
      </div>
    </div>
  );
}

export const DEFAULT_INDICATOR_COLORS = DEFAULTS;
